import React, { useState, useEffect } from 'react';
import { FileText, Edit, Brain, Target, Download, Zap, Globe } from 'lucide-react';
import MathematicsTopics from './MathematicsTopics';
import TemplateWizard from './TemplateWizard';
import FlashCardsGenerator from './FlashCardsGenerator';
import QuizletIntegration from './QuizletIntegration';
import GeneratedResults from './GeneratedResults';
import RightMathsBrain from './RightMathsBrain';
import RightMathsZone from './RightMathsZone';

const MathematicsTopicsContainer = ({
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  categories,
  filteredTopics,
  autofillFromTemplate,
  generateFlashCardsFromTopic,
  startQuizFromTopic,
  templateData,
  handleTemplateChange,
  templateTypes,
  usefulExpressions,
  resetTemplate,
  setActiveTab,
  generatePDF,
  selectedTopicForFlashCards,
  setSelectedTopicForFlashCards,
  selectedTopicForQuiz,
  setSelectedTopicForQuiz
}) => {
  const [activeSubTab, setActiveSubTab] = useState('rightmaths-brain');
  const [showTabText, setShowTabText] = useState(() => {
    const saved = localStorage.getItem('showTabText');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Listen for tab display changes from the floating control
  useEffect(() => {
    const handleTabDisplayChange = (event) => {
      setShowTabText(event.detail.showText);
    };

    window.addEventListener('tabDisplayChanged', handleTabDisplayChange);
    
    return () => {
      window.removeEventListener('tabDisplayChanged', handleTabDisplayChange);
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Sub-tab Navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            {[
              { id: 'rightmaths-brain', label: '數學右腦', icon: Zap },
              { id: 'rightmaths-zone', label: '挑戰區', icon: Globe },
              { id: 'topics', label: '數學課題', icon: FileText },
              { id: 'wizard', label: '模板精靈', icon: Edit },
              { id: 'flashcards', label: '閃卡', icon: Brain },
              { id: 'quiz', label: '測驗', icon: Target },
              { id: 'generated', label: '生成結果', icon: Download }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeSubTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  title={showTabText ? undefined : tab.label}
                >
                  <Icon className="w-4 h-4" />
                  {showTabText && <span>{tab.label}</span>}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Sub-tab Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Topics Sub-tab */}
        {activeSubTab === 'topics' && (
          <MathematicsTopics
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
            filteredTopics={filteredTopics}
            autofillFromTemplate={autofillFromTemplate}
            generateFlashCardsFromTopic={generateFlashCardsFromTopic}
            startQuizFromTopic={startQuizFromTopic}
          />
        )}

        {/* Template Wizard Sub-tab */}
        {activeSubTab === 'wizard' && (
          <TemplateWizard
            templateData={templateData}
            handleTemplateChange={handleTemplateChange}
            templateTypes={templateTypes}
            usefulExpressions={usefulExpressions}
            resetTemplate={resetTemplate}
          />
        )}

        {/* Flash Cards Sub-tab */}
        {activeSubTab === 'flashcards' && (
          <FlashCardsGenerator
            onBack={() => {
              setSelectedTopicForFlashCards(null);
              setActiveSubTab('topics');
            }}
            selectedTopic={selectedTopicForFlashCards}
          />
        )}

        {/* Quiz Sub-tab */}
        {activeSubTab === 'quiz' && (
          <QuizletIntegration
            onBack={() => {
              setSelectedTopicForQuiz(null);
              setActiveSubTab('topics');
            }}
            selectedTopic={selectedTopicForQuiz}
          />
        )}

        {/* Generated Results Sub-tab */}
        {activeSubTab === 'generated' && (
          <GeneratedResults
            templateData={templateData}
            generatePDF={generatePDF}
          />
        )}

        {/* RightMaths Brain Sub-tab */}
        {activeSubTab === 'rightmaths-brain' && (
          <RightMathsBrain />
        )}

        {/* RightMaths Zone Sub-tab */}
        {activeSubTab === 'rightmaths-zone' && (
          <RightMathsZone />
        )}
      </div>
    </div>
  );
};

export default MathematicsTopicsContainer; 